import { t } from 'translations'

// Every string is a getter or a function, so it is translated when it is read, never when this module loads.

const quote = (text: string) => t('capacity.quote', { text })

export const SECTION = {
    get title() {
        return t('capacity.section.title')
    },
    get backToList() {
        return t('capacity.section.backToList')
    },
    get reportLink() {
        return t('capacity.section.reportLink')
    },
    get newTitle() {
        return t('capacity.section.newTitle')
    },
    get renameNotice() {
        return t('capacity.section.renameNotice')
    },
    get dismiss() {
        return t('capacity.section.dismiss')
    },
}

export const blastRadius = () => t('capacity.blastRadius')

export const SAVE = {
    get idle() {
        return t('capacity.save.idle')
    },
    get create() {
        return t('capacity.save.create')
    },
    get keepEditing() {
        return t('capacity.save.keepEditing')
    },
    get confirm() {
        return t('capacity.save.confirm')
    },
    get saving() {
        return t('capacity.save.saving')
    },
    get nothingChanged() {
        return t('capacity.save.nothingChanged')
    },
    get nameMissing() {
        return t('capacity.save.nameMissing')
    },
    saved: (name: string) => t('capacity.save.saved', { name: quote(name) }),
    created: (name: string) => t('capacity.save.created', { name: quote(name) }),
    get failed() {
        return t('capacity.save.failed')
    },
    partlyFailed: (names: string[]) => t('capacity.save.partlyFailed', { names: names.map(quote).join(', ') }),
    label: (parts: string[]) =>
        parts.length === 0
            ? t('capacity.save.label.none')
            : parts.length <= 2
            ? t('capacity.save.label.parts', { parts: parts.join(', ') })
            : t('capacity.save.label.many', { count: parts.length }),
    retryLabel: (count: number) => t('capacity.save.retryLabel', { count }),
    parts: {
        get rename() {
            return t('capacity.save.parts.rename')
        },
        create: (name: string) => t('capacity.save.parts.create', { name: quote(name) }),
        limit: (from: string, to: string) => t('capacity.save.parts.limit', { from, to }),
        add: (count: number) => t('capacity.save.parts.add', { count }),
        remove: (count: number) => t('capacity.save.parts.remove', { count }),
        uses: (name: string, from: number, to: number) => t('capacity.save.parts.uses', { name, from, to }),
        usesMany: (count: number) => t('capacity.save.parts.usesMany', { count }),
    },
    unsaved: {
        get title() {
            return t('capacity.save.unsaved.title')
        },
        get text() {
            return t('capacity.save.unsaved.text')
        },
        get leave() {
            return t('capacity.save.unsaved.leave')
        },
        get stay() {
            return t('capacity.save.unsaved.stay')
        },
    },
}

export const CONFIG = {
    get heading() {
        return t('capacity.config.heading')
    },
    get name() {
        return t('capacity.config.name')
    },
    get nameHelp() {
        return t('capacity.config.nameHelp')
    },
    get namePlaceholder() {
        return t('capacity.config.namePlaceholder')
    },
    get limit() {
        return t('capacity.config.limit')
    },
    get limitHelp() {
        return t('capacity.config.limitHelp')
    },
    get limitPlaceholder() {
        return t('capacity.config.limitPlaceholder')
    },
    get limitInvalid() {
        return t('capacity.config.limitInvalid')
    },
    get closed() {
        return t('capacity.config.closed')
    },
    belowUsed: (used: number, next: number) => t('capacity.config.belowUsed', { used, next }),
    belowUsedClosed: (used: number) => t('capacity.config.belowUsedClosed', { used }),
    get noDefaultRule() {
        return t('capacity.config.noDefaultRule')
    },
    get howItCounts() {
        return t('capacity.config.howItCounts')
    },
    get howItCountsQuestion() {
        return t('capacity.config.howItCountsQuestion')
    },
    counting: {
        wholeTimeSlot: {
            get lead() {
                return t('capacity.config.counting.wholeTimeSlot.lead')
            },
            get example() {
                return t('capacity.config.counting.wholeTimeSlot.example')
            },
        },
        arrivalOnly: {
            get lead() {
                return t('capacity.config.counting.arrivalOnly.lead')
            },
            get example() {
                return t('capacity.config.counting.arrivalOnly.example')
            },
        },
    },
    countsLabel: {
        get wholeTimeSlot() {
            return t('capacity.config.countsLabel.wholeTimeSlot')
        },
        get arrivalOnly() {
            return t('capacity.config.countsLabel.arrivalOnly')
        },
    },
    countsShort: {
        get wholeTimeSlot() {
            return t('capacity.config.countsShort.wholeTimeSlot')
        },
        get arrivalOnly() {
            return t('capacity.config.countsShort.arrivalOnly')
        },
    },
}

export const LIMIT = {
    get noLimit() {
        return t('capacity.limit.noLimit')
    },
    get closed() {
        return t('capacity.limit.closed')
    },
    get unknown() {
        return t('capacity.limit.unknown')
    },
}

export const PRODUCTS = {
    get heading() {
        return t('capacity.products.heading')
    },
    get addPlaceholder() {
        return t('capacity.products.addPlaceholder')
    },
    get addNoneLeft() {
        return t('capacity.products.addNoneLeft')
    },
    get bundleCannotBeAdded() {
        return t('capacity.products.bundleCannotBeAdded')
    },
    get passCannotBeAdded() {
        return t('capacity.products.passCannotBeAdded')
    },
    get filterType() {
        return t('capacity.products.filterType')
    },
    get filterOnSale() {
        return t('capacity.products.filterOnSale')
    },
    get filterAll() {
        return t('capacity.products.filterAll')
    },
    get filterOptions() {
        return t('capacity.products.filterOptions')
    },
    get filterTickets() {
        return t('capacity.products.filterTickets')
    },
    get filterYes() {
        return t('capacity.products.filterYes')
    },
    get filterNo() {
        return t('capacity.products.filterNo')
    },
    get showAll() {
        return t('capacity.products.showAll')
    },
    count: (shown: number, total: number) => t('capacity.products.count', { shown, count: total }),
    columns: {
        get product() {
            return t('capacity.products.columns.product')
        },
        get onSale() {
            return t('capacity.products.columns.onSale')
        },
        get takesUp() {
            return t('capacity.products.columns.takesUp')
        },
        get uses() {
            return t('capacity.products.columns.uses')
        },
        get visitorsEach() {
            return t('capacity.products.columns.visitorsEach')
        },
    },
    get takesUpTooltip() {
        return t('capacity.products.takesUpTooltip')
    },
    get usesTooltip() {
        return t('capacity.products.usesTooltip')
    },
    takesUp: {
        get slotEnd() {
            return t('capacity.products.takesUp.slotEnd')
        },
        get slotStart() {
            return t('capacity.products.takesUp.slotStart')
        },
        get dayEnd() {
            return t('capacity.products.takesUp.dayEnd')
        },
        get dayStart() {
            return t('capacity.products.takesUp.dayStart')
        },
        get dependsOnCategory() {
            return t('capacity.products.takesUp.dependsOnCategory')
        },
        get dependsOnDate() {
            return t('capacity.products.takesUp.dependsOnDate')
        },
        get bundle() {
            return t('capacity.products.takesUp.bundle')
        },
        get unavailable() {
            return t('capacity.products.takesUp.unavailable')
        },
    },
    onSale: {
        get yes() {
            return t('capacity.products.onSale.yes')
        },
        get no() {
            return t('capacity.products.onSale.no')
        },
        none: '—',
        get optionTooltip() {
            return t('capacity.products.onSale.optionTooltip')
        },
        factors: {
            get enabled() {
                return t('capacity.products.onSale.factors.enabled')
            },
            get showDates() {
                return t('capacity.products.onSale.factors.showDates')
            },
            get validTo() {
                return t('capacity.products.onSale.factors.validTo')
            },
            get category() {
                return t('capacity.products.onSale.factors.category')
            },
        },
        shownFrom: (date: string) => t('capacity.products.onSale.shownFrom', { date }),
        shownUntil: (date: string) => t('capacity.products.onSale.shownUntil', { date }),
        validUntil: (date: string) => t('capacity.products.onSale.validUntil', { date }),
    },
    breadcrumb: {
        get none() {
            return t('capacity.products.breadcrumb.none')
        },
        many: (count: number) => t('capacity.products.breadcrumb.many', { count }),
        separator: ' › ',
    },
    tags: {
        get new() {
            return t('capacity.products.tags.new')
        },
        get changed() {
            return t('capacity.products.tags.changed')
        },
        get removing() {
            return t('capacity.products.tags.removing')
        },
        get notSaved() {
            return t('capacity.products.tags.notSaved')
        },
    },
    stepper: {
        more: (name: string) => t('capacity.products.stepper.more', { name: quote(name) }),
        less: (name: string) => t('capacity.products.stepper.less', { name: quote(name) }),
        get atMin() {
            return t('capacity.products.stepper.atMin')
        },
        get atMax() {
            return t('capacity.products.stepper.atMax')
        },
        get perCategory() {
            return t('capacity.products.stepper.perCategory')
        },
    },
    menu: {
        get remove() {
            return t('capacity.products.menu.remove')
        },
        get dontAdd() {
            return t('capacity.products.menu.dontAdd')
        },
        get undoRemove() {
            return t('capacity.products.menu.undoRemove')
        },
        get viewProduct() {
            return t('capacity.products.menu.viewProduct')
        },
        get viewOptionItem() {
            return t('capacity.products.menu.viewOptionItem')
        },
        get viewTimeSlots() {
            return t('capacity.products.menu.viewTimeSlots')
        },
        productId: (id: string | number) => t('capacity.products.menu.productId', { id }),
        get productIdLoading() {
            return t('capacity.products.menu.productIdLoading')
        },
        get productIdLoadingReason() {
            return t('capacity.products.menu.productIdLoadingReason')
        },
        get copied() {
            return t('capacity.products.menu.copied')
        },
        get copyFailed() {
            return t('capacity.products.menu.copyFailed')
        },
        more: (name: string) => t('capacity.products.menu.more', { name: quote(name) }),
    },
    get empty() {
        return t('capacity.products.empty')
    },
    get filteredEmptyProducts() {
        return t('capacity.products.filteredEmpty.products')
    },
    filteredEmpty: (what: string, sale: 'all' | 'yes' | 'no') =>
        sale === 'yes'
            ? t('capacity.products.filteredEmpty.onSale', { what })
            : sale === 'no'
            ? t('capacity.products.filteredEmpty.notOnSale', { what })
            : t('capacity.products.filteredEmpty.all', { what }),
    get error() {
        return t('capacity.products.error')
    },
    get retry() {
        return t('capacity.products.retry')
    },
    get optionItemsCapped() {
        return t('capacity.products.optionItemsCapped')
    },
    get optionItemsFailed() {
        return t('capacity.products.optionItemsFailed')
    },
    get linkedTwice() {
        return t('capacity.products.linkedTwice')
    },
    get rowNotSaved() {
        return t('capacity.products.rowNotSaved')
    },
    get changedElsewhere() {
        return t('capacity.products.changedElsewhere')
    },
    get loadingProducts() {
        return t('capacity.products.loadingProducts')
    },
    get actions() {
        return t('capacity.products.actions')
    },
    consequence: {
        wholeTimeSlot: (dayProducts: string[], slotProducts: string[]) =>
            t('capacity.products.consequence.wholeTimeSlot', {
                count: dayProducts.length,
                dayProducts: list(dayProducts),
                slotProducts: list(slotProducts),
            }),
        arrivalOnly: (dayProducts: string[], slotProducts: string[]) =>
            t('capacity.products.consequence.arrivalOnly', {
                count: dayProducts.length,
                dayProducts: list(dayProducts),
                slotProducts: list(slotProducts),
            }),
    },
}

export const EXCEPTIONS = {
    get heading() {
        return t('capacity.exceptions.heading')
    },
    get add() {
        return t('capacity.exceptions.add')
    },
    get none() {
        return t('capacity.exceptions.none')
    },
}

export const LIST = {
    get capacities() {
        return t('capacity.list.capacities')
    },
    get capacitiesMeaning() {
        return t('capacity.list.capacitiesMeaning')
    },
    get search() {
        return t('capacity.list.search')
    },
    get clearSearch() {
        return t('capacity.list.clearSearch')
    },
    get createNew() {
        return t('capacity.list.createNew')
    },
    columns: {
        get name() {
            return t('capacity.list.columns.name')
        },
        get limit() {
            return t('capacity.list.columns.limit')
        },
        get counts() {
            return t('capacity.list.columns.counts')
        },
        get products() {
            return t('capacity.list.columns.products')
        },
    },
    get countsTooltip() {
        return t('capacity.list.countsTooltip')
    },
    products: (count: number, options: number) =>
        options > 0 ? t('capacity.list.productsWithOptions', { products: count, count: options }) : `${count}`,
    get empty() {
        return t('capacity.list.empty')
    },
    noMatch: (query: string) => t('capacity.list.noMatch', { query: quote(query) }),
    get error() {
        return t('capacity.list.error')
    },
    get cannotDelete() {
        return t('capacity.list.cannotDelete')
    },
}

export const FORM_SECTION = {
    get heading() {
        return t('capacity.formSection.heading')
    },
    get capacities() {
        return t('capacity.formSection.capacities')
    },
    get timeSlots() {
        return t('capacity.formSection.timeSlots')
    },
    usesOfLimit: (amount: number) => t('capacity.formSection.usesOfLimit', { amount }),
    get booksPerSlot() {
        return t('capacity.formSection.booksPerSlot')
    },
    get booksWholeDay() {
        return t('capacity.formSection.booksWholeDay')
    },
    get notInAny() {
        return t('capacity.formSection.notInAny')
    },
    get bundle() {
        return t('capacity.formSection.bundle')
    },
    get bundleTimeSlots() {
        return t('capacity.formSection.bundleTimeSlots')
    },
    get saveProductFirst() {
        return t('capacity.formSection.saveProductFirst')
    },
    get saveCategoryFirst() {
        return t('capacity.formSection.saveCategoryFirst')
    },
    categoryProducts: (count: number) => t('capacity.formSection.categoryProducts', { count }),
    get categoryNone() {
        return t('capacity.formSection.categoryNone')
    },
    get setOnProduct() {
        return t('capacity.formSection.setOnProduct')
    },
    get setOnCategory() {
        return t('capacity.formSection.setOnCategory')
    },
    fromCategory: (name: string) => t('capacity.formSection.fromCategory', { name: quote(name) }),
    fromParentCategory: (name: string) => t('capacity.formSection.fromParentCategory', { name: quote(name) }),
    get defaultForAll() {
        return t('capacity.formSection.defaultForAll')
    },
    get viaCategory() {
        return t('capacity.formSection.viaCategory')
    },
    get noTimeSlots() {
        return t('capacity.formSection.noTimeSlots')
    },
    get switchedOff() {
        return t('capacity.formSection.switchedOff')
    },
    get noGroupProduct() {
        return t('capacity.formSection.noGroupProduct')
    },
    get noGroupCategory() {
        return t('capacity.formSection.noGroupCategory')
    },
    get goToTimeSlots() {
        return t('capacity.formSection.goToTimeSlots')
    },
    get editTimeSlots() {
        return t('capacity.formSection.editTimeSlots')
    },
    get timesLoading() {
        return t('capacity.formSection.timesLoading')
    },
    get timesFailed() {
        return t('capacity.formSection.timesFailed')
    },
    get groupsFailed() {
        return t('capacity.formSection.groupsFailed')
    },
    get enabled() {
        return t('capacity.formSection.enabled')
    },
    offWarning: (count: number) => t('capacity.formSection.offWarning', { count }),
    get offWarningUnknown() {
        return t('capacity.formSection.offWarningUnknown')
    },
    get offLine() {
        return t('capacity.formSection.offLine')
    },
    get everyDay() {
        return t('capacity.formSection.everyDay')
    },
    andMore: (count: number) => t('capacity.formSection.andMore', { count }),
}

export const TIME_SLOTS = {
    get search() {
        return t('capacity.timeSlots.search')
    },
    kind: {
        get category() {
            return t('capacity.timeSlots.kind.category')
        },
        get product() {
            return t('capacity.timeSlots.kind.product')
        },
    },
    views: {
        get categories() {
            return t('capacity.timeSlots.views.categories')
        },
        get products() {
            return t('capacity.timeSlots.views.products')
        },
    },
    get viewBusy() {
        return t('capacity.timeSlots.viewBusy')
    },
    status: {
        get active() {
            return t('capacity.timeSlots.status.active')
        },
        get upcoming() {
            return t('capacity.timeSlots.status.upcoming')
        },
        get expired() {
            return t('capacity.timeSlots.status.expired')
        },
    },
    get all() {
        return t('capacity.timeSlots.all')
    },
    get appliesTo() {
        return t('capacity.timeSlots.appliesTo')
    },
    get showAll() {
        return t('capacity.timeSlots.showAll')
    },
    emptyForSelection: (name: string) => t('capacity.timeSlots.emptyForSelection', { name: quote(name) }),
    emptyForStatus: (status: string) => t('capacity.timeSlots.emptyForStatus', { status: status.toLowerCase() }),
    emptyForBoth: (status: string, name: string) =>
        t('capacity.timeSlots.emptyForBoth', { status: status.toLowerCase(), name: quote(name) }),
    get none() {
        return t('capacity.timeSlots.none')
    },
}

export function list(names: string[]): string {
    if (names.length === 0) {
        return ''
    }
    if (names.length === 1) {
        return names[0]
    }
    if (names.length === 2) {
        return t('capacity.listJoin.two', { first: names[0], second: names[1] })
    }
    const shown = names.slice(0, 2)
    return t('capacity.listJoin.more', { names: shown.join(', '), count: names.length - shown.length })
}

export { quote }
